// ===================================================================
// 🔐 PÁGINA DE LOGIN - Ingressa
// ===================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { styles } from '../../styles/globalStyles';
import { EmailInboxHint } from '../../components/auth/EmailInboxHint';
import { Alert } from '../../components/ui/Alert';
import { Modal } from '../../components/ui/Modal';
import { apiClient } from '../../api/apiClient';
import { friendlyNetworkError } from '../../utils/friendlyErrors';
import { SYSTEM_DISPLAY_NAME } from '../../utils/ingressaBrand';
import { downloadBlobFile } from '../../utils/helpers';
import { OwnerLoginWatermark } from '../../components/auth/OwnerLoginWatermark';
import { OwnerBrandFooter } from '../../components/auth/OwnerBrandFooter';
import { CandidatePwaInstallBanner } from '../../components/pwa/CandidatePwaInstallBanner';
import { CandidatePwaUpdateBanner } from '../../components/pwa/CandidatePwaUpdateBanner';
import { syncCandidatePwaBranding } from '../../pwa/candidatePwa';
import { applyTenantPwaFromConfig } from '../../pwa/tenantPwaContract';
import {
  blocksStaffInCandidatePwa,
  CANDIDATE_PWA_STAFF_BLOCK_MESSAGE,
} from '../../pwa/candidatePwa';

import {
  CANDIDATE_DOC_ACCEPT,
  CANDIDATE_DOC_FORMATS_LABEL,
  isAllowedCandidateDocumentFile,
} from '../../utils/candidateDocumentUpload';

const DEFAULT_SYSTEM_ICON = '/static/ingressa-logo.svg';

function isImageUrl(url) {
  return url && (url.startsWith('http') || url.startsWith('/'));
}

export const LoginScreen = React.memo(({ setView, onLoginSuccess, setRegisterType, forcedMessage = '' }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [config, setConfig] = useState({
    institution_name: 'IFNMG',
    system_name: SYSTEM_DISPLAY_NAME,
    institution_icon: '',
    institution_icon_right: '',
    platform_owner_icon: '',
    platform_system_icon: '',
    platform_system_sigla: '',
    platform_system_name: '',
    is_platform_owner: false,
  });
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [challengeOpenProcesses, setChallengeOpenProcesses] = useState([]);
  const [hasProtocolLookup, setHasProtocolLookup] = useState(false);
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [showProtocolModal, setShowProtocolModal] = useState(false);
  const [challengeForm, setChallengeForm] = useState({
    process_id: '',
    submitter_name: '',
    submitter_cpf: '',
    submitter_email: '',
    submitter_phone: '',
    justification: '',
  });
  const [challengeDocs, setChallengeDocs] = useState([]);
  const [processEditais, setProcessEditais] = useState([]);
  const [processEditaisLoading, setProcessEditaisLoading] = useState(false);
  const [challengeSubmitting, setChallengeSubmitting] = useState(false);
  const [challengeModalError, setChallengeModalError] = useState('');
  const [challengeSuccessProtocol, setChallengeSuccessProtocol] = useState('');
  const [receiptDownloading, setReceiptDownloading] = useState(false);
  const [protocolQuery, setProtocolQuery] = useState('');
  const [protocolResult, setProtocolResult] = useState(null);
  const [protocolLoading, setProtocolLoading] = useState(false);
  const [protocolError, setProtocolError] = useState('');

  useEffect(() => {
    apiClient.getConfig()
      .then((data) => {
        const next = {
          institution_name: data.institution_name || 'IFNMG',
          system_name: data.system_name || SYSTEM_DISPLAY_NAME,
          institution_icon: data.institution_icon || '',
          institution_icon_right: data.institution_icon_right || '',
          platform_owner_icon: data.platform_owner_icon || '',
          platform_system_icon: data.platform_system_icon || '',
          platform_system_sigla: data.platform_system_sigla || '',
          platform_system_name: data.platform_system_name || '',
          is_platform_owner: !!data.is_platform_owner,
        };
        setConfig(next);
        applyTenantPwaFromConfig(data);
        syncCandidatePwaBranding(next);
        if (!data.is_platform_owner) {
          apiClient.getOpenEditalChallengeProcesses()
            .then(({ data: openData }) => {
              setChallengeOpenProcesses(Array.isArray(openData?.processes) ? openData.processes : []);
              setHasProtocolLookup(Boolean(openData?.has_protocol_lookup));
            })
            .catch(() => {
              setChallengeOpenProcesses([]);
              setHasProtocolLookup(false);
            });
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!forcedMessage) return;
    if (/conclu[ií]do|sucesso/i.test(forcedMessage)) {
      setSuccessMessage(forcedMessage);
      setError('');
    } else {
      setError(forcedMessage);
      setSuccessMessage('');
    }
  }, [forcedMessage]);

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError('');
  }, [error]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data, error: authError } = await apiClient.auth.signIn({
        email: formData.email,
        password: formData.password
      });

      if (authError) {
        setError(friendlyNetworkError(authError?.message || 'Erro ao fazer login.'));
      } else if (data?.user) {
        if (blocksStaffInCandidatePwa(data.user.role)) {
          await apiClient.auth.signOut();
          setError(CANDIDATE_PWA_STAFF_BLOCK_MESSAGE);
        } else {
          onLoginSuccess(data.user);
        }
      }
    } catch (err) {
      setError(friendlyNetworkError('Erro ao fazer login. Tente novamente.'));
    } finally {
      setLoading(false);
    }
  }, [formData, onLoginSuccess]);

  const handleForgotPassword = useCallback(async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return;
    setLoading(true);
    const { error: err } = await apiClient.auth.forgotPassword(forgotEmail);
    setLoading(false);
    if (err) setError(err);
    else setForgotSuccess(true);
  }, [forgotEmail]);

  const openChallengeModal = useCallback(() => {
    setChallengeModalError('');
    setChallengeSuccessProtocol('');
    setChallengeDocs([]);
    setProcessEditais([]);
    const firstId = challengeOpenProcesses[0]?.id ? String(challengeOpenProcesses[0].id) : '';
    setChallengeForm({
      process_id: firstId,
      submitter_name: '',
      submitter_cpf: '',
      submitter_email: '',
      submitter_phone: '',
      justification: '',
    });
    setShowChallengeModal(true);
  }, [challengeOpenProcesses]);

  useEffect(() => {
    const pid = Number(challengeForm.process_id);
    if (!showChallengeModal || !pid) {
      setProcessEditais([]);
      return undefined;
    }
    let cancelled = false;
    setProcessEditaisLoading(true);
    apiClient.getPublicEditalChallengeProcessDocuments(pid)
      .then(({ data, error: err }) => {
        if (cancelled) return;
        if (err) {
          setProcessEditais([]);
          return;
        }
        setProcessEditais(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setProcessEditais([]);
      })
      .finally(() => {
        if (!cancelled) setProcessEditaisLoading(false);
      });
    return () => { cancelled = true; };
  }, [showChallengeModal, challengeForm.process_id]);

  const handleChallengeFile = useCallback(async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setChallengeModalError('');
    if (!isAllowedCandidateDocumentFile(file)) {
      setChallengeModalError(`Formato inválido. Use ${CANDIDATE_DOC_FORMATS_LABEL}.`);
      return;
    }
    try {
      const uploaded = await apiClient.uploadEditalChallengeDocument(file);
      setChallengeDocs((prev) => [...prev, { name: uploaded.name || file.name, url: uploaded.url }]);
    } catch (err) {
      setChallengeModalError(err.message || 'Erro no upload');
    }
  }, []);

  const handleSubmitChallenge = useCallback(async (e) => {
    e.preventDefault();
    setChallengeModalError('');
    setChallengeSubmitting(true);
    try {
      const { data, error: err } = await apiClient.submitEditalChallenge({
        process_id: Number(challengeForm.process_id),
        submitter_name: challengeForm.submitter_name,
        submitter_cpf: challengeForm.submitter_cpf,
        submitter_email: challengeForm.submitter_email,
        submitter_phone: challengeForm.submitter_phone,
        justification: challengeForm.justification,
        documents: challengeDocs,
      });
      if (err) throw new Error(err);
      setChallengeSuccessProtocol(data?.protocol || '');
    } catch (err) {
      setChallengeModalError(err.message || 'Não foi possível enviar a impugnação');
    } finally {
      setChallengeSubmitting(false);
    }
  }, [challengeForm, challengeDocs]);

  const formatChallengeCpf = useCallback((value) => {
    const n = String(value || '').replace(/\D/g, '').slice(0, 11);
    if (n.length <= 3) return n;
    if (n.length <= 6) return `${n.slice(0, 3)}.${n.slice(3)}`;
    if (n.length <= 9) return `${n.slice(0, 3)}.${n.slice(3, 6)}.${n.slice(6)}`;
    return `${n.slice(0, 3)}.${n.slice(3, 6)}.${n.slice(6, 9)}-${n.slice(9)}`;
  }, []);

  const formatChallengePhone = useCallback((value) => {
    const n = String(value || '').replace(/\D/g, '').slice(0, 11);
    if (n.length === 0) return '';
    if (n.length <= 2) return `(${n}`;
    if (n.length <= 6) return `(${n.slice(0, 2)}) ${n.slice(2)}`;
    if (n.length <= 10) return `(${n.slice(0, 2)}) ${n.slice(2, 6)}-${n.slice(6)}`;
    return `(${n.slice(0, 2)}) ${n.slice(2, 7)}-${n.slice(7)}`;
  }, []);

  const handleDownloadChallengeReceipt = useCallback(async (protocol) => {
    const proto = (protocol || '').trim();
    if (!proto || receiptDownloading) return;
    setReceiptDownloading(true);
    try {
      const { blob, filename } = await apiClient.downloadEditalChallengeReceipt(proto);
      downloadBlobFile(blob, filename);
    } catch (err) {
      setChallengeModalError(err.message || 'Não foi possível baixar o comprovante');
      setProtocolError(err.message || 'Não foi possível baixar o comprovante');
    } finally {
      setReceiptDownloading(false);
    }
  }, [receiptDownloading]);

  const handleQueryProtocol = useCallback(async (e) => {
    e.preventDefault();
    setProtocolError('');
    setProtocolResult(null);
    const proto = (protocolQuery || '').trim();
    if (!proto) {
      setProtocolError('Informe o protocolo');
      return;
    }
    setProtocolLoading(true);
    try {
      const { data, error: err } = await apiClient.getEditalChallengeByProtocol(proto);
      if (err) throw new Error(err);
      setProtocolResult(data);
    } catch (err) {
      setProtocolError(err.message || 'Protocolo não encontrado');
    } finally {
      setProtocolLoading(false);
    }
  }, [protocolQuery]);

  const ownerCompanyIcon = config.platform_owner_icon;
  const hasOwnerCompanyIcon = isImageUrl(ownerCompanyIcon);
  const systemIcon = isImageUrl(config.platform_system_icon)
    ? config.platform_system_icon
    : DEFAULT_SYSTEM_ICON;
  const systemSigla = (config.platform_system_sigla || '').trim();
  const tenantSigla = (config.institution_name || '').trim();
  const isOwnerLogin = config.is_platform_owner;
  const isClientTenantLogin = !isOwnerLogin;
  const hasClientLeftLogo = isImageUrl(config.institution_icon);
  const hasClientRightLogo = isImageUrl(config.institution_icon_right);
  const showClientLogoRow = isClientTenantLogin && (hasClientLeftLogo || hasClientRightLogo);
  const showChallengeActions = !isOwnerLogin && (challengeOpenProcesses.length > 0 || hasProtocolLookup);

  const openProtocolModal = () => {
    setProtocolQuery('');
    setProtocolResult(null);
    setProtocolError('');
    setShowProtocolModal(true);
  };

  return (
    <div
      style={{
        ...styles.authContainer,
        position: 'relative',
        overflow: 'hidden',
        minHeight: '100vh',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        padding: 0,
      }}
    >
      {isOwnerLogin && <OwnerLoginWatermark iconUrl={ownerCompanyIcon} />}

      <div
        style={{
          position: 'relative',
          zIndex: 1,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          padding: isClientTenantLogin ? '24px 20px 8px' : '24px 20px',
        }}
      >
        <div style={styles.authBox}>
          <h1 style={{ ...styles.authTitle, marginBottom: '8px' }}>
            {isOwnerLogin && (
              <>
                {hasOwnerCompanyIcon && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      marginBottom: 20,
                      minHeight: 100,
                    }}
                  >
                    <img
                      src={ownerCompanyIcon}
                      alt=""
                      style={{
                        maxWidth: 'min(280px, 90%)',
                        maxHeight: 120,
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                      }}
                    />
                  </div>
                )}
                {systemSigla && (
                  <p
                    style={{
                      textAlign: 'center',
                      margin: '0 0 12px',
                      fontSize: 15,
                      fontWeight: 700,
                      color: '#374151',
                      letterSpacing: '0.02em',
                    }}
                  >
                    {systemSigla}
                  </p>
                )}
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
                  <img
                    src={systemIcon}
                    alt=""
                    style={{ width: 72, height: 72, objectFit: 'contain' }}
                  />
                </div>
              </>
            )}

            <span
              style={{
                textAlign: 'center',
                display: 'block',
                fontSize: '22px',
                lineHeight: 1.15,
                letterSpacing: '0',
                fontWeight: 700,
                maxWidth: '320px',
                margin: isClientTenantLogin ? '0 auto 20px' : '0 auto',
                textWrap: 'balance',
                wordBreak: 'normal',
              }}
            >
              {config.system_name}
            </span>
          </h1>

          {showClientLogoRow && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 20,
                minHeight: 80,
                gap: 12,
              }}
            >
              {hasClientLeftLogo ? (
                <img
                  src={config.institution_icon}
                  alt=""
                  style={{ width: 80, height: 80, objectFit: 'contain', flexShrink: 0 }}
                />
              ) : (
                <span style={{ width: 80, flexShrink: 0 }} />
              )}
              {hasClientRightLogo ? (
                <img
                  src={config.institution_icon_right}
                  alt=""
                  style={{ width: 80, height: 80, objectFit: 'contain', flexShrink: 0 }}
                />
              ) : (
                <span style={{ width: 80, flexShrink: 0 }} />
              )}
            </div>
          )}

          {showChallengeActions ? (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                justifyContent: 'center',
                marginBottom: '18px',
              }}
            >
              {challengeOpenProcesses.length > 0 && (
                <button
                  type="button"
                  onClick={openChallengeModal}
                  style={{
                    ...styles.button,
                    ...styles.buttonSecondary,
                    ...styles.buttonSmall,
                    padding: '7px 12px',
                    fontSize: '12px',
                    lineHeight: 1.2,
                  }}
                >
                  📢 Impugnar edital
                </button>
              )}
              {hasProtocolLookup && (
                <button
                  type="button"
                  onClick={openProtocolModal}
                  style={{
                    ...styles.button,
                    ...styles.buttonSecondary,
                    ...styles.buttonSmall,
                    padding: '7px 12px',
                    fontSize: '12px',
                    lineHeight: 1.2,
                  }}
                >
                  Consultar protocolo
                </button>
              )}
            </div>
          ) : (
            <div style={{ marginBottom: '18px' }} />
          )}

          {successMessage && (
            <Alert
              message={successMessage}
              type="success"
              onClose={() => setSuccessMessage('')}
            />
          )}

          {error && (
            <Alert
              message={error}
              type="error"
              onClose={() => setError('')}
            />
          )}

          {isClientTenantLogin && (
            <>
              <CandidatePwaUpdateBanner />
              <CandidatePwaInstallBanner institutionLabel={tenantSigla || config.institution_name} />
            </>
          )}

          <form onSubmit={handleSubmit} style={styles.form}>
            <div>
              <label style={styles.label}>Email</label>
              <input
                type="email"
                name="username"
                autoComplete="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                style={styles.input}
                placeholder="seu@email.com"
                required
              />
            </div>

            <div>
              <label style={styles.label}>Senha</label>
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                style={styles.input}
                placeholder="Sua senha"
                required
              />
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginTop: '8px',
                fontSize: '14px',
                color: '#6B7280',
                cursor: 'pointer',
                userSelect: 'none'
              }}>
                <input
                  type="checkbox"
                  checked={showPassword}
                  onChange={(e) => setShowPassword(e.target.checked)}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
                Ver senha
              </label>
              <div style={{ textAlign: 'right', marginTop: '8px' }}>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#3B82F6',
                    cursor: 'pointer',
                    fontSize: '13px',
                    textDecoration: 'underline'
                  }}
                >
                  Esqueci a senha
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                ...styles.button,
                ...styles.buttonPrimary,
                width: '100%',
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Entrando...' : 'Entrar'}
            </button>
          </form>

          {!isOwnerLogin && (
          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            <button
              onClick={() => {
                setRegisterType('candidate');
                setView('register');
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#3B82F6',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
            >
              Sou candidato - Registre-se
            </button>
          </div>
          )}
        </div>
      </div>

      {isClientTenantLogin && (
        <div style={{ position: 'relative', zIndex: 1, width: '100%', flexShrink: 0 }}>
          <OwnerBrandFooter iconUrl={ownerCompanyIcon} poweredBy />
        </div>
      )}

      <Modal isOpen={showForgotPassword} onClose={() => { setShowForgotPassword(false); setForgotEmail(''); setForgotSuccess(false); setError(''); }} title="Recuperar senha">
        {forgotSuccess ? (
          <div>
            <p style={{ color: '#374151', marginBottom: 10, lineHeight: 1.6, fontSize: 14 }}>
              Se o e-mail estiver cadastrado, enviamos instruções para criar uma nova senha.
              O link expira em <strong>24 horas</strong>.
            </p>
            <EmailInboxHint />
            <button
              onClick={() => { setShowForgotPassword(false); setForgotSuccess(false); }}
              style={{ ...styles.button, ...styles.buttonPrimary, width: '100%', marginTop: 16 }}
            >
              Fechar
            </button>
          </div>
        ) : (
          <form onSubmit={handleForgotPassword} style={styles.form}>
            <p style={{ color: '#6B7280', marginBottom: '16px' }}>
              Informe seu email para receber o link de recuperação de senha.
            </p>
            <div>
              <label style={styles.label}>Email</label>
              <input
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                style={styles.input}
                placeholder="seu@email.com"
                required
              />
            </div>
            {error && <Alert message={error} type="error" onClose={() => setError('')} />}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setShowForgotPassword(false)} style={{ ...styles.button, ...styles.buttonSecondary }}>
                Cancelar
              </button>
              <button type="submit" disabled={loading} style={{ ...styles.button, ...styles.buttonPrimary }}>
                {loading ? 'Enviando...' : 'Enviar'}
              </button>
            </div>
          </form>
        )}
      </Modal>

      <Modal
        isOpen={showChallengeModal}
        onClose={() => !challengeSubmitting && setShowChallengeModal(false)}
        title="Impugnação ao edital"
      >
        {challengeSuccessProtocol ? (
          <div>
            <Alert
              message={`Impugnação registrada. Guarde o protocolo: ${challengeSuccessProtocol}`}
              type="success"
            />
            <p style={{ color: '#374151', fontSize: 14, lineHeight: 1.5, marginBottom: 12 }}>
              Use o protocolo para consultar o andamento. Se quiser, salve também o PDF com o texto que você enviou.
            </p>
            <button
              type="button"
              disabled={receiptDownloading}
              onClick={() => handleDownloadChallengeReceipt(challengeSuccessProtocol)}
              style={{
                ...styles.button,
                ...styles.buttonSecondary,
                width: '100%',
                marginBottom: 8,
                opacity: receiptDownloading ? 0.7 : 1,
              }}
            >
              {receiptDownloading ? 'Gerando PDF…' : '💾 Salvar PDF da impugnação'}
            </button>
            <button
              type="button"
              onClick={() => setShowChallengeModal(false)}
              style={{ ...styles.button, ...styles.buttonPrimary, width: '100%' }}
            >
              Fechar
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmitChallenge} style={styles.form}>
            {challengeModalError && (
              <Alert message={challengeModalError} type="error" onClose={() => setChallengeModalError('')} />
            )}
            <div>
              <label style={styles.label}>Processo seletivo</label>
              <select
                value={challengeForm.process_id}
                onChange={(e) => setChallengeForm((p) => ({ ...p, process_id: e.target.value }))}
                style={styles.input}
                required
              >
                <option value="">Selecione…</option>
                {challengeOpenProcesses.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            {challengeForm.process_id && (
              <div style={{
                marginBottom: 4,
                padding: 12,
                background: '#F8FAFC',
                border: '1px solid #E2E8F0',
                borderRadius: 8,
              }}>
                <p style={{ margin: '0 0 8px', fontWeight: 600, fontSize: 14, color: '#334155' }}>
                  Editais e anexos do processo
                </p>
                {processEditaisLoading ? (
                  <p style={{ margin: 0, fontSize: 13, color: '#64748B' }}>Carregando…</p>
                ) : processEditais.length === 0 ? (
                  <p style={{ margin: 0, fontSize: 13, color: '#64748B' }}>
                    Nenhum edital/anexo cadastrado ainda pelo administrador.
                  </p>
                ) : (
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                    {processEditais.map((doc) => (
                      <li key={doc.id} style={{ marginBottom: 6 }}>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const { blob, filename } = await apiClient.downloadPublicEditalChallengeProcessDocument(doc.id);
                              downloadBlobFile(blob, filename || doc.file_name || doc.name || 'edital.pdf');
                            } catch (err) {
                              setChallengeModalError(err.message || 'Erro ao baixar documento');
                            }
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#2563EB',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            padding: 0,
                            textAlign: 'left',
                          }}
                        >
                          {doc.name || doc.file_name || `Documento ${doc.id}`}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <div>
              <label style={styles.label}>Nome completo</label>
              <input
                value={challengeForm.submitter_name}
                onChange={(e) => setChallengeForm((p) => ({ ...p, submitter_name: e.target.value }))}
                style={styles.input}
                required
              />
            </div>
            <div>
              <label style={styles.label}>CPF (opcional)</label>
              <input
                value={challengeForm.submitter_cpf}
                onChange={(e) => setChallengeForm((p) => ({ ...p, submitter_cpf: formatChallengeCpf(e.target.value) }))}
                style={styles.input}
                placeholder="000.000.000-00"
                inputMode="numeric"
                autoComplete="off"
                maxLength={14}
              />
            </div>
            <div>
              <label style={styles.label}>E-mail</label>
              <input
                type="email"
                value={challengeForm.submitter_email}
                onChange={(e) => setChallengeForm((p) => ({ ...p, submitter_email: e.target.value }))}
                style={styles.input}
                required
              />
            </div>
            <div>
              <label style={styles.label}>Telefone (opcional)</label>
              <input
                value={challengeForm.submitter_phone}
                onChange={(e) => setChallengeForm((p) => ({ ...p, submitter_phone: formatChallengePhone(e.target.value) }))}
                style={styles.input}
                placeholder="(00) 00000-0000"
                inputMode="tel"
                autoComplete="tel"
                maxLength={15}
              />
            </div>
            <div>
              <label style={styles.label}>Justificativa</label>
              <textarea
                value={challengeForm.justification}
                onChange={(e) => setChallengeForm((p) => ({ ...p, justification: e.target.value }))}
                style={{ ...styles.input, minHeight: 100, resize: 'vertical' }}
                required
                minLength={20}
              />
            </div>
            <div>
              <label style={styles.label}>Anexos (opcional — {CANDIDATE_DOC_FORMATS_LABEL})</label>
              <input type="file" accept={CANDIDATE_DOC_ACCEPT} onChange={handleChallengeFile} />
              {challengeDocs.length > 0 && (
                <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 13 }}>
                  {challengeDocs.map((d, i) => (
                    <li key={i}>
                      {d.name}{' '}
                      <button
                        type="button"
                        onClick={() => setChallengeDocs((prev) => prev.filter((_, idx) => idx !== i))}
                        style={{ border: 'none', background: 'none', color: '#DC2626', cursor: 'pointer' }}
                      >
                        remover
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                type="button"
                disabled={challengeSubmitting}
                onClick={() => setShowChallengeModal(false)}
                style={{ ...styles.button, ...styles.buttonSecondary }}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={challengeSubmitting}
                style={{ ...styles.button, ...styles.buttonPrimary }}
              >
                {challengeSubmitting ? 'Enviando…' : 'Protocolar impugnação'}
              </button>
            </div>
          </form>
        )}
      </Modal>

      <Modal
        isOpen={showProtocolModal}
        onClose={() => setShowProtocolModal(false)}
        title="Consultar protocolo de impugnação"
      >
        <form onSubmit={handleQueryProtocol} style={styles.form}>
          {protocolError && <Alert message={protocolError} type="error" onClose={() => setProtocolError('')} />}
          <div>
            <label style={styles.label}>Protocolo</label>
            <input
              value={protocolQuery}
              onChange={(e) => setProtocolQuery(e.target.value)}
              style={styles.input}
              placeholder="Ex.: IMP-1-000001"
              required
            />
          </div>
          <button
            type="submit"
            disabled={protocolLoading}
            style={{ ...styles.button, ...styles.buttonPrimary }}
          >
            {protocolLoading ? 'Consultando…' : 'Consultar'}
          </button>
        </form>
        {protocolResult && (
          <div style={{ marginTop: 16, padding: 12, background: '#F9FAFB', borderRadius: 8, border: '1px solid #E5E7EB' }}>
            <p><strong>Protocolo:</strong> {protocolResult.protocol}</p>
            <p><strong>Processo:</strong> {protocolResult.process_name}</p>
            <p><strong>Status:</strong> {protocolResult.status_label || protocolResult.status}</p>
            {protocolResult.justification && (
              <p style={{ whiteSpace: 'pre-wrap' }}><strong>Sua impugnação:</strong> {protocolResult.justification}</p>
            )}
            {protocolResult.admin_response && (
              <p style={{ whiteSpace: 'pre-wrap' }}><strong>Resposta:</strong> {protocolResult.admin_response}</p>
            )}
            <button
              type="button"
              disabled={receiptDownloading}
              onClick={() => handleDownloadChallengeReceipt(protocolResult.protocol)}
              style={{
                ...styles.button,
                ...styles.buttonSecondary,
                ...styles.buttonSmall,
                marginTop: 10,
                opacity: receiptDownloading ? 0.7 : 1,
              }}
            >
              {receiptDownloading ? 'Gerando PDF…' : '💾 Salvar PDF da impugnação'}
            </button>
          </div>
        )}
      </Modal>
    </div>
  );
});
